import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { CallMemberRole } from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { PopupStore } from '@gem/popups';

import { ICallAddSpeakerLinkProps } from './CallAddSpeakerLink.types';

interface ICallAddSpeakerLinkPresenter {
  childPopup: PopupStore | null;
  activeSpeakersIds: string[];
  ongoingCall: Call;
  channelId: string | null;
  onChangeSpeakers(userIds: []): void;
  onClick(): void;
  onClose(): void;
  setActiveSpeakersIds(userIds: string[]): void;
}

export const useCallAddSpeakerLinkPresenter = (
  props: ICallAddSpeakerLinkProps
): ICallAddSpeakerLinkPresenter => {
  const presenter = useLocalObservable<ICallAddSpeakerLinkPresenter>(() => ({
    childPopup: null,
    activeSpeakersIds: props.activeSpeakersIds,

    get ongoingCall(): Call {
      return props.call;
    },

    get channelId(): string | null {
      return presenter.ongoingCall.channelId;
    },

    onClose(): void {
      props.onClose?.();
    },

    async onChangeSpeakers(userIds: string[]): Promise<void> {
      if (presenter.channelId) {
        const removedSpeakers = presenter.activeSpeakersIds.filter(
          (userId) => !userIds.includes(userId)
        );
        const addedSpeakers = userIds.filter(
          (userId) =>
            !presenter.activeSpeakersIds.includes(userId) && !removedSpeakers.includes(userId)
        );
        const participantIdsToRole = [
          ...removedSpeakers.map((userId) => ({
            userId,
            role: CallMemberRole.Member,
          })),
          ...addedSpeakers.map((userId) => ({
            userId,
            role: CallMemberRole.Superuser,
          })),
        ];
        await props.callController.setCallParticipantsRoles(
          presenter.channelId,
          participantIdsToRole
        );
      }
    },

    onClick(): void {
      if (props.callController.handlers.onAddCallSpeakers) {
        presenter.childPopup = props.callController.handlers.onAddCallSpeakers({
          maxSpeakers: presenter.ongoingCall.maxBroadcasters,
          activeSpeakerIds: presenter.activeSpeakersIds,
          onSubmit: presenter.onChangeSpeakers,
          onClose: presenter.onClose,
          onBack: () => props.popupInstance.show(),
        });
      }
    },

    setActiveSpeakersIds(userIds: string[]): void {
      presenter.activeSpeakersIds = userIds;
    },
  }));

  useEffect(() => {
    presenter.setActiveSpeakersIds(props.activeSpeakersIds);
  }, [props.activeSpeakersIds]);

  return presenter;
};
